import { useState } from 'react';
import { PACK, QUESTION } from '@constants/terminology';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useGetWorkgroupsQuery } from '@api/workgroups';
import { SelectFactory } from '@g17eco/molecules/select/SelectFactory';
import { Workgroup, SurveyWorkgroup } from '@g17eco/types/workgroup';
import { getDefaultScope, getModulesFromScope, getScopeFromModules, getScopeModules } from '@utils/survey-scope';
import { MetricGroup } from '@g17eco/types/metricGroup';
import { Toggle } from '@g17eco/molecules/toggle';
import { SurveyUserRoles } from '@constants/user';
import { DataScopeAccess } from '@g17eco/types/surveyDataScope';
import { generateToast, toastFromError } from '@g17eco/molecules/toasts';
import { SurveyScope } from '@g17eco/types/surveyCommon';
import { Option, SelectTypes } from '@g17eco/molecules/select/types';
import { WorkgroupIcon } from '@g17eco/molecules/workgroup/WorkgroupIcon';
import { SurveyPermission } from '@g17eco/types/permission';
import { capitalize } from '@utils/string';

interface Form {
  workgroupId?: string;
  includeAllModules: boolean;
  modules: string[];
  permission?: SurveyUserRoles | 'contributor-verifier';
  isAdmin: boolean;
}

const getFormPermission = (editingWorkgroup?: SurveyWorkgroup) => {
  if (!editingWorkgroup) {
    return;
  }

  const isVerifier = editingWorkgroup.permission.roles.includes(SurveyUserRoles.Verifier);
  const isContributor = editingWorkgroup.permission.roles.includes(SurveyUserRoles.Stakeholder);

  if (isVerifier && isContributor) {
    return 'contributor-verifier';
  }

  if (isContributor) {
    return SurveyUserRoles.Stakeholder;
  }

  if (isVerifier) {
    return SurveyUserRoles.Verifier;
  }

  return;
};

const getForm = (editingWorkgroup?: SurveyWorkgroup): Form => {
  return {
    workgroupId: editingWorkgroup?._id,
    includeAllModules: editingWorkgroup?.permission.access === DataScopeAccess.Full,
    modules: getModulesFromScope(editingWorkgroup?.permission.scope ?? {}),
    permission: getFormPermission(editingWorkgroup),
    isAdmin: Boolean(editingWorkgroup?.permission.roles.includes(SurveyUserRoles.Admin)),
  };
};

const getWorkgroupOptions = ({
  initiativeWorkgroups,
  delegatedWorkgroups,
  editingWorkgroup,
}: {
  initiativeWorkgroups: Workgroup[];
  delegatedWorkgroups: SurveyWorkgroup[];
  editingWorkgroup?: SurveyWorkgroup;
}) => {
  return initiativeWorkgroups
    .filter(
      (workgroup) =>
        workgroup._id === editingWorkgroup?._id ||
        !delegatedWorkgroups.some((delegatedWorkgroup) => delegatedWorkgroup._id === workgroup._id),
    )
    .map((workgroup) => ({
      value: workgroup._id,
      label: (
        <>
          <WorkgroupIcon workgroup={workgroup} />
          {workgroup.name}
        </>
      ),
    }));
};

const getModuleOptions = (scope: SurveyScope, metricGroups: MetricGroup[]) => {
  return getScopeModules({ scope, metricGroups }).map((item) => ({
    value: item.code,
    label: item.name,
  }));
};

const getPermissionOptions = (): Option<SurveyUserRoles | 'contributor-verifier'>[] => {
  return [
    { value: 'contributor-verifier', label: 'Contributor and verifier' },
    { value: SurveyUserRoles.Stakeholder, label: 'Contributor only' },
    { value: SurveyUserRoles.Verifier, label: 'Verifier only' },
  ];
};

const getAdminOptions = (): Option<boolean>[] => {
  return [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' },
  ];
};

interface DelegateWorkgroupEntity {
  _id: string;
  initiativeId: string;
  scope?: SurveyScope;
}

type PermissionData = Pick<SurveyPermission, 'access' | 'scope' | 'roles'>

interface DelegateWorkgroupHandlers {
  onDelegate: (params: { workgroupIds: string[]; permission: PermissionData }) => Promise<void>;
  onUpdate: (params: { workgroupId: string; permission: PermissionData }) => Promise<void>;
}

interface Props {
  isOpen: boolean;
  toggle: () => void;
  entity: DelegateWorkgroupEntity;
  metricGroups: MetricGroup[];
  delegatedWorkgroups: SurveyWorkgroup[];
  editingWorkgroup?: SurveyWorkgroup;
  handlers: DelegateWorkgroupHandlers;
  entityLabel: string;
  isLoading?: boolean;
}

export const DelegateWorkgroupModalBase = ({
  isOpen,
  toggle,
  entity,
  metricGroups,
  delegatedWorkgroups,
  editingWorkgroup,
  handlers,
  entityLabel,
  isLoading = false,
}: Props) => {
  const { data: initiativeWorkgroups = [], isFetching: isFetchingWorkgroups } = useGetWorkgroupsQuery(
    entity.initiativeId,
  );
  const [form, setForm] = useState<Form>(getForm(editingWorkgroup));

  const handleChangeForm = (changes: Partial<Form>) => {
    setForm((state) => {
      return {
        ...state,
        ...changes,
      };
    });
  };

  const workgroupOptions = getWorkgroupOptions({ initiativeWorkgroups, delegatedWorkgroups, editingWorkgroup });
  const moduleOptions = getModuleOptions(entity.scope ?? getDefaultScope(), metricGroups);
  const permissionOptions = getPermissionOptions();
  const adminOptions = getAdminOptions();

  const hasModules = form.includeAllModules || form.modules.length > 0;
  const isDisabled = isFetchingWorkgroups || isLoading || !form.workgroupId || !hasModules || !form.permission;

  const handleSubmit = () => {
    if (!form.workgroupId || !hasModules || !form.permission) {
      return;
    }

    const access = form.includeAllModules ? DataScopeAccess.Full : DataScopeAccess.Partial;
    const scope = getScopeFromModules(form.modules);
    const roles =
      form.permission === 'contributor-verifier'
        ? [SurveyUserRoles.Stakeholder, SurveyUserRoles.Verifier]
        : [form.permission];
    if (form.isAdmin) {
      roles.push(SurveyUserRoles.Admin);
    }

    const permission: PermissionData = { access, scope, roles };

    if (editingWorkgroup) {
      return handlers
        .onUpdate({
          workgroupId: editingWorkgroup._id,
          permission,
        })
        .then(() => {
          generateToast({
            color: 'success',
            message: 'Workgroup updated successfully',
          });
          toggle();
        })
        .catch((e) => {
          toastFromError({ error: e, title: 'Failed to update workgroup' });
        });
    }

    return handlers
      .onDelegate({
        workgroupIds: [form.workgroupId],
        permission,
      })
      .then(() => {
        generateToast({
          color: 'success',
          message: 'Workgroup delegated successfully',
        });
        toggle();
      })
      .catch((e) => {
        toastFromError({ error: e, title: 'Failed to delegate workgroup' });
      });
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} backdrop='static' returnFocusAfterClose={false}>
      <ModalHeader toggle={toggle}>Delegate workgroup to {entityLabel}</ModalHeader>
      <ModalBody>
        <h6 className='text-ThemeHeadingDark fw-bold'>Workgroup</h6>
        <p className='text-ThemeTextMedium mb-1'>
          Select which workgroup you would like to delegate to this {entityLabel}.
        </p>
        <SelectFactory
          placeholder='Select workgroup'
          selectType={SelectTypes.SingleSelect}
          options={workgroupOptions}
          value={workgroupOptions.find((workgroup) => workgroup.value === form.workgroupId)}
          onChange={(option) => handleChangeForm({ workgroupId: option?.value })}
          isSearchable={false}
          isDisabled={!!editingWorkgroup}
        />

        <h6 className='text-ThemeHeadingDark fw-bold mt-3'>{capitalize(entityLabel)} scope</h6>
        <p className='text-ThemeTextMedium mb-1'>Select which {PACK.PLURAL} these users should have access to.</p>
        <Toggle
          checked={form.includeAllModules}
          onChange={() => handleChangeForm({ includeAllModules: !form.includeAllModules })}
          label={`Delegate to entire ${entityLabel} scope`}
        />
        <SelectFactory
          selectType={SelectTypes.MultipleSelect}
          options={moduleOptions}
          onChange={(modules) => handleChangeForm({ modules })}
          values={form.modules}
          placeholder={`Select ${entityLabel} scope modules`}
          isDisabled={form.includeAllModules}
        />

        <h6 className='text-ThemeHeadingDark fw-bold mt-3'>{QUESTION.CAPITALIZED_SINGULAR} permission</h6>
        <p className='text-ThemeTextMedium mb-1'>
          Select what users within this workgroup can do with these {PACK.PLURAL}.
        </p>
        <SelectFactory
          placeholder={`Select ${QUESTION.SINGULAR} permission`}
          selectType={SelectTypes.SingleSelect}
          options={permissionOptions}
          value={permissionOptions.find((option) => option.value === form.permission)}
          onChange={(option) => handleChangeForm({ permission: option?.value })}
          isSearchable={false}
        />

        <h6 className='text-ThemeHeadingDark fw-bold mt-3'>{capitalize(entityLabel)} admin</h6>
        <p className='text-ThemeTextMedium mb-1'>
          Select whether these users need admin level access to this {entityLabel}.
        </p>
        <SelectFactory
          selectType={SelectTypes.SingleSelect}
          options={adminOptions}
          value={adminOptions.find((option) => option.value === form.isAdmin)}
          onChange={(option) => handleChangeForm({ isAdmin: option?.value })}
          isSearchable={false}
        />
      </ModalBody>
      <ModalFooter>
        <Button color='link-secondary' onClick={toggle}>
          Cancel
        </Button>
        <Button color='primary' onClick={handleSubmit} disabled={isDisabled}>
          Add workgroup
        </Button>
      </ModalFooter>
    </Modal>
  );
};
